import { injectable } from "inversify";
import { __NEXTAUTH, getCsrfToken, getSession, signOut } from "next-auth/react";

import { type Credentials } from "../../application";
import { type Session } from "../../domain";
import { NextAuthProvider } from "./next-auth.provider";

@injectable()
export class NextAuthClientProvider extends NextAuthProvider {
  private renewSessionPromise?: Promise<Session | null>;
  private signOutPromise?: Promise<{ success: boolean }>;
  private session: Session | null = null;

  async signIn(credentials: Credentials) {
    // Clone next-auth/react signIn and modified for our use
    const csrfToken = await getCsrfToken();
    const response = await fetch(`/api/auth/callback/credentials`, {
      method: "post",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-Auth-Return-Redirect": "1",
      },
      body: new URLSearchParams([...this.toFormFields(credentials), ["csrfToken", csrfToken]]),
    });

    const data = await response.json();
    const url = new URL(data.url);
    const error = url.searchParams.get("error");
    const code = url.searchParams.get("code");

    if (error && code) {
      try {
        const { pendingToken } = JSON.parse(code);
        return { success: false, pendingToken: String(pendingToken) };
        // eslint-disable-next-line sonarjs/no-ignored-exceptions
      } catch (error) {
        return { success: false };
      }
    }

    await __NEXTAUTH._getSession({ event: "storage" });

    if (credentials.type === "with-self-signup") {
      const session = await getSession();
      return { success: true, ...session?.selfSignUp };
    }

    return { success: true };
  }

  async signOut() {
    const callback = async () => {
      await signOut({ redirect: false });
      return { success: true };
    };

    if (!this.signOutPromise) {
      this.signOutPromise = callback().finally(() => {
        this.signOutPromise = undefined;
      });
    }

    return this.signOutPromise;
  }

  async getSession(): Promise<Session | null> {
    return this.session ? this.session : this.renewSession();
  }

  async updateSession(nextAuthSession: Session): Promise<void> {
    const newSession = this.toSession(nextAuthSession);
    if (newSession && !newSession.accessToken.isExpired()) {
      this.session = newSession;
    }
    return Promise.resolve();
  }

  async renewSession(): Promise<Session | null> {
    const callback = async () => {
      const nextAuthSession = await getSession();
      this.session = nextAuthSession && this.toSession(nextAuthSession);
      return this.session;
    };

    // eslint-disable-next-line sonarjs/no-misused-promises
    if (!this.renewSessionPromise) {
      this.renewSessionPromise = callback().finally(() => {
        this.renewSessionPromise = undefined;
      });
    }

    return this.renewSessionPromise;
  }
}
